import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  BookOpen,
  Rocket,
  Layers,
  Lightbulb,
  CornerDownLeft,
  FileText,
} from 'lucide-react';
import { Command as CommandPrimitive } from 'cmdk';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface SearchItem {
  titleKey: string;
  slug: string;
  sectionKey: string;
  descriptionKey: string;
  keywords?: string[];
}

const sectionConfig: Record<string, { icon: typeof BookOpen; color: string }> = {
  'sections.introduction': { icon: BookOpen, color: 'text-primary' },
  'sections.features': { icon: Layers, color: 'text-primary' },
  'sections.guides': { icon: Lightbulb, color: 'text-primary' },
  'sections.updates': { icon: Rocket, color: 'text-primary' },
};

const searchItems: SearchItem[] = [
  // Introdução
  {
    titleKey: 'pages.what_is.title',
    slug: 'o-que-e',
    sectionKey: 'sections.introduction',
    descriptionKey: 'pages.what_is.description',
    keywords: ['sobre', 'plataforma', 'sistema', 'conceitos', 'benefícios', 'vantagens', 'about', 'platform', 'system'],
  },
  // Funcionalidades
  {
    titleKey: 'pages.overview.title',
    slug: 'visao-geral',
    sectionKey: 'sections.features',
    descriptionKey: 'pages.overview.description',
    keywords: ['painel', 'dashboard', 'home', 'início', 'overview', 'telas', 'screens'],
  },
  {
    titleKey: 'pages.projects.title',
    slug: 'projetos',
    sectionKey: 'sections.features',
    descriptionKey: 'pages.projects.description',
    keywords: ['gerenciar', 'organizar', 'lista', 'criar projeto', 'manage', 'organize', 'projects'],
  },
  {
    titleKey: 'pages.meetings.title',
    slug: 'atas-de-reuniao',
    sectionKey: 'sections.features',
    descriptionKey: 'pages.meetings.description',
    keywords: ['reuniões', 'documentar', 'registrar', 'primeira ata', 'meetings', 'minutes'],
  },
  {
    titleKey: 'pages.topics_items.title',
    slug: 'topicos-e-itens',
    sectionKey: 'sections.features',
    descriptionKey: 'pages.topics_items.description',
    keywords: ['pauta', 'agenda', 'assuntos', 'discussão', 'topics', 'items'],
  },
  {
    titleKey: 'pages.developments.title',
    slug: 'desenvolvimentos',
    sectionKey: 'sections.features',
    descriptionKey: 'pages.developments.description',
    keywords: ['progresso', 'atualização', 'histórico', 'clonagem', 'pendentes', 'progress', 'updates'],
  },
  {
    titleKey: 'pages.invite_team.title',
    slug: 'convidar-equipe',
    sectionKey: 'sections.features',
    descriptionKey: 'pages.invite_team.description',
    keywords: ['membros', 'colaboradores', 'convite', 'time', 'compartilhar', 'team', 'invite', 'share'],
  },
  {
    titleKey: 'pages.minutes_map.title',
    slug: 'mapa-de-atas',
    sectionKey: 'sections.features',
    descriptionKey: 'pages.minutes_map.description',
    keywords: ['visualizar', 'histórico', 'timeline', 'map', 'view'],
  },
  {
    titleKey: 'pages.customization.title',
    slug: 'personalizacao',
    sectionKey: 'sections.features',
    descriptionKey: 'pages.customization.description',
    keywords: ['logo', 'empresa', 'configuração', 'tópicos padrão', 'cabeçalho', 'customize', 'settings'],
  },
  {
    titleKey: 'pages.preview_export.title',
    slug: 'preview-e-exportacao',
    sectionKey: 'sections.features',
    descriptionKey: 'pages.preview_export.description',
    keywords: ['pdf', 'imprimir', 'download', 'visualizar', 'export', 'print'],
  },
  // Guias
  {
    titleKey: 'pages.best_practices.title',
    slug: 'boas-praticas-atas',
    sectionKey: 'sections.guides',
    descriptionKey: 'pages.best_practices.description',
    keywords: ['dicas', 'melhores práticas', 'eficiência', 'follow-up', 'tips', 'best practices'],
  },
  {
    titleKey: 'pages.organization_collaboration.title',
    slug: 'organizacao-e-colaboracao',
    sectionKey: 'sections.guides',
    descriptionKey: 'pages.organization_collaboration.description',
    keywords: ['organização', 'estrutura', 'gestão', 'trabalho em equipe', 'organization', 'teamwork'],
  },
  // Atualizações
  {
    titleKey: 'pages.news.title',
    slug: 'novidades',
    sectionKey: 'sections.updates',
    descriptionKey: 'pages.news.description',
    keywords: ['changelog', 'atualizações', 'releases', 'versões', 'updates', 'news'],
  },
];

const sectionKeys = ['sections.introduction', 'sections.features', 'sections.guides', 'sections.updates'];

function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;

  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="bg-primary/20 text-primary rounded-sm px-0.5">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

export function DocsSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation('docs');
  const { t: tCommon } = useTranslation('common');
  const appName = tCommon('app_name');

  // Helper to translate with appName
  const translateWithApp = useCallback((key: string) => {
    return t(key, { appName });
  }, [t, appName]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const runCommand = useCallback((command: () => void) => {
    setOpen(false);
    setQuery('');
    command();
  }, []);

  const filteredSections = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return sectionKeys;

    return sectionKeys.filter((sectionKey) => {
      const items = searchItems.filter((item) => item.sectionKey === sectionKey);
      return items.some(
        (item) =>
          translateWithApp(item.titleKey).toLowerCase().includes(q) ||
          translateWithApp(item.descriptionKey).toLowerCase().includes(q) ||
          item.keywords?.some((k) => k.toLowerCase().includes(q))
      );
    });
  }, [query, translateWithApp]);

  const getFilteredItems = useCallback(
    (sectionKey: string) => {
      const q = query.toLowerCase().trim();
      const items = searchItems.filter((item) => item.sectionKey === sectionKey);

      if (!q) return items.map((item) => ({ ...item, matchedIn: null as string | null }));

      return items
        .map((item) => {
          const matchedIn: string[] = [];
          const title = translateWithApp(item.titleKey);
          const description = translateWithApp(item.descriptionKey);

          if (title.toLowerCase().includes(q)) matchedIn.push('título');
          if (description.toLowerCase().includes(q)) matchedIn.push('descrição');
          const matchedKeyword = item.keywords?.find((k) => k.toLowerCase().includes(q));
          if (matchedKeyword) matchedIn.push(matchedKeyword);

          if (matchedIn.length > 0) {
            return { ...item, matchedIn: matchedIn[0] };
          }
          return null;
        })
        .filter((item): item is SearchItem & { matchedIn: string | null } => item !== null);
    },
    [query, translateWithApp]
  );

  const totalResults = useMemo(() => {
    return filteredSections.reduce((acc, section) => acc + getFilteredItems(section).length, 0);
  }, [filteredSections, getFilteredItems]);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-8 w-44 justify-start gap-2 text-muted-foreground"
        onClick={() => setOpen(true)}
      >
        <Search className="size-4" />
        <span>{t('search.placeholder')}</span>
      </Button>

      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-xl translate-x-[-50%] translate-y-[-50%] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
            <CommandPrimitive
              className="flex h-full w-full flex-col"
              filter={(value, search) => {
                const item = searchItems.find(
                  (i) => `${translateWithApp(i.titleKey)} ${t(i.sectionKey)}`.toLowerCase() === value.toLowerCase()
                );
                if (!item) return 0;
                const q = search.toLowerCase();
                if (
                  translateWithApp(item.titleKey).toLowerCase().includes(q) ||
                  translateWithApp(item.descriptionKey).toLowerCase().includes(q) ||
                  item.keywords?.some((k) => k.toLowerCase().includes(q))
                ) {
                  return 1;
                }
                return 0;
              }}
            >
              {/* Search Input */}
              <div className="flex items-center gap-3 border-b border-border px-4 py-3">
                <Search className="size-5 shrink-0 text-muted-foreground" />
                <CommandPrimitive.Input
                  value={query}
                  onValueChange={setQuery}
                  placeholder={t('search.input_placeholder')}
                  className="flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground"
                />
                {query && (
                  <button
                    onClick={() => setQuery('')}
                    className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                  >
                    {t('search.clear')}
                  </button>
                )}
              </div>

              {/* Results */}
              <CommandPrimitive.List className="max-h-[400px] overflow-y-auto overflow-x-hidden pt-2">
                {/* Empty State */}
                <CommandPrimitive.Empty className="py-12 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="rounded-full bg-muted p-3">
                      <FileText className="size-6 text-muted-foreground" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-foreground">
                        {t('search.no_results', { query })}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('search.try_another')}
                      </p>
                    </div>
                  </div>
                </CommandPrimitive.Empty>

                {/* Results by Section */}
                {filteredSections.map((sectionKey) => {
                  const items = getFilteredItems(sectionKey);
                  if (items.length === 0) return null;

                  const config = sectionConfig[sectionKey];
                  const SectionIcon = config?.icon || FileText;

                  return (
                    <CommandPrimitive.Group
                      key={sectionKey}
                      heading={
                        <div className="flex items-center gap-2 px-2 py-2">
                          <SectionIcon className={cn('size-4', config?.color)} />
                          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            {t(sectionKey)}
                          </span>
                          <span className="text-xs text-muted-foreground/60">
                            ({items.length})
                          </span>
                        </div>
                      }
                      className="px-2 pb-2"
                    >
                      {items.map((item) => {
                        const ItemIcon = sectionConfig[item.sectionKey]?.icon || FileText;
                        const iconColor = sectionConfig[item.sectionKey]?.color;
                        const showMatchIndicator = query && item.matchedIn && item.matchedIn !== 'título' && item.matchedIn !== 'descrição';

                        return (
                          <CommandPrimitive.Item
                            key={item.slug}
                            value={`${translateWithApp(item.titleKey)} ${t(item.sectionKey)}`}
                            onSelect={() => {
                              runCommand(() => navigate(`/docs/${item.slug}`));
                            }}
                            className="group relative flex cursor-pointer select-none items-start gap-3 rounded-lg px-3 py-3 outline-none transition-colors data-[selected=true]:bg-accent"
                          >
                            <div
                              className={cn(
                                'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted transition-colors',
                                'group-data-[selected=true]:bg-primary/10'
                              )}
                            >
                              <ItemIcon
                                className={cn(
                                  'size-4 transition-colors',
                                  iconColor,
                                  'group-data-[selected=true]:text-primary'
                                )}
                              />
                            </div>
                            <div className="flex-1 space-y-0.5 overflow-hidden">
                              <div className="flex items-center gap-2">
                                <p className="truncate text-sm font-medium text-foreground">
                                  {highlightMatch(translateWithApp(item.titleKey), query)}
                                </p>
                                {showMatchIndicator && (
                                  <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                                    {item.matchedIn}
                                  </span>
                                )}
                              </div>
                              <p className="truncate text-xs text-muted-foreground">
                                {highlightMatch(translateWithApp(item.descriptionKey), query)}
                              </p>
                            </div>
                            <CornerDownLeft className="mt-2 size-4 shrink-0 text-muted-foreground/40 opacity-0 transition-opacity group-data-[selected=true]:opacity-100" />
                          </CommandPrimitive.Item>
                        );
                      })}
                    </CommandPrimitive.Group>
                  );
                })}
              </CommandPrimitive.List>

              {/* Footer - Results count */}
              {query && totalResults > 0 && (
                <div className="border-t border-border bg-muted/30 px-4 py-2">
                  <span className="text-xs text-muted-foreground">
                    {t('search.results_found', { count: totalResults })}
                  </span>
                </div>
              )}
            </CommandPrimitive>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
}
